/**
 * Dynamic checkout loader for lazy loading the checkout component
 *
 * This module provides a function to dynamically load and display
 * the checkout overlay when a user clicks "Book Now".
 *
 * Benefits:
 * - Smaller initial bundle size for timetable widget
 * - Checkout code only loaded when needed
 * - Cached after first load for subsequent bookings
 */

type CheckoutConfig = {
  listingId: string;
  businessId: string;
  apiUrl?: string;
  primaryColor?: string;
  theme?: "light" | "dark";
  widgetBaseUrl?: string;
  /** Pre-select a specific session on mount (skips date selection) */
  initialSessionId?: string;
};

// Cache for the checkout component to avoid re-downloading
let checkoutLoaded = false;

/**
 * Opens the checkout overlay for a listing
 *
 * Dynamically creates a baluu-checkout-overlay element and appends it to the document body.
 * The checkout overlay handles the full booking flow including payment.
 *
 * @param config - Configuration for the checkout
 * @returns Promise that resolves when checkout is opened
 */
export async function openCheckout(config: CheckoutConfig): Promise<void> {
  const {
    listingId,
    businessId,
    apiUrl,
    primaryColor,
    theme,
    widgetBaseUrl,
    initialSessionId,
  } = config;

  // Check if the checkout element is already registered
  if (!customElements.get("baluu-checkout-overlay")) {
    // The checkout overlay should already be registered when the main widget loaded
    // But if not, we can't dynamically import it since it's part of the same bundle
    console.warn(
      "baluu-checkout-overlay not registered. Ensure the widget script is loaded.",
    );
  }

  // Create and configure the checkout overlay element
  const overlay = document.createElement("baluu-checkout-overlay");

  // Set required attributes
  overlay.setAttribute("listing-id", listingId);
  overlay.setAttribute("business-id", businessId);

  // Set optional attributes
  if (apiUrl) overlay.setAttribute("api-url", apiUrl);
  if (primaryColor) overlay.setAttribute("primary-color", primaryColor);
  if (theme) overlay.setAttribute("theme", theme);
  if (widgetBaseUrl) overlay.setAttribute("widget-base-url", widgetBaseUrl);
  if (initialSessionId)
    overlay.setAttribute("initial-session-id", initialSessionId);

  // Append to body (outside any Shadow DOM for proper layering)
  document.body.appendChild(overlay);

  checkoutLoaded = true;

  // Listen for close events to clean up
  const handleClose = () => {
    if (overlay.parentNode) {
      overlay.parentNode.removeChild(overlay);
    }
    document.removeEventListener("baluu:checkout-close", handleClose);
    document.removeEventListener("baluu:booking-completed", handleComplete);
  };

  const handleComplete = () => {
    // Dispatch event that can be caught by the parent page
    const event = new CustomEvent("baluu:timetable-booking-completed", {
      bubbles: true,
      composed: true,
      detail: { listingId, businessId },
    });
    document.dispatchEvent(event);

    // Close after a delay to show success message
    setTimeout(handleClose, 3000);
  };

  document.addEventListener("baluu:checkout-close", handleClose);
  document.addEventListener("baluu:booking-completed", handleComplete);
}

/**
 * Check if checkout has been loaded
 */
export function isCheckoutLoaded(): boolean {
  return checkoutLoaded;
}

